import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminSalesContentDeleteAction,
  adminSalesContentListAction
} from "../Action/action";
import DeleteConfirmModal from "./delete-confirm-modal";

export default function SalesContentList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminSalesContentListAction();
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setError(res?.message || "Failed to load sales content.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setError(e?.response?.data?.message || e?.message || "Failed to load.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadNonce]);

  const onDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminSalesContentDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || "Delete failed");
      toast.success("Deleted", { toastId: "sales-del" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Delete failed", {
        toastId: "sales-del"
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page-content content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">{error}</div>
        </div>
      ) : null}

      <div className="page-header-row toolbar">
        <div>
          <h2 className="section-title" style={{ margin: 0 }}>
            Sales Content
          </h2>
          <p className="small-label" style={{ marginTop: 6 }}>
            Content for the Brave Brain Academy sales / enrol screen in the app.
          </p>
        </div>
        <Link className="btn btn-primary" to="/sales-content/new">
          + Add sales content
        </Link>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Brand title</th>
                <th>Tagline</th>
                <th>Enrol button</th>
                <th>Status</th>
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5}>Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5}>No sales content yet. Click “+ Add sales content”.</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.brand_title || "—"}</td>
                    <td>{row.tagline || "—"}</td>
                    <td>{row.enrol_button_label || "—"}</td>
                    <td>
                      <span className={Number(row.status) === 1 ? "badge badge-success" : "badge"}>
                        {Number(row.status) === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Link className="action-link" to={`/sales-content/${row.id}/edit`}>
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="action-link text-danger"
                          style={{ background: "transparent", border: "none", padding: 0 }}
                          onClick={() => {
                            setDeleteRow(row);
                            setDeleteOpen(true);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        title="Confirm Delete"
        itemType="sales content"
        itemName={deleteRow ? deleteRow.brand_title : ""}
        confirmDisabled={deleting}
        onCancel={() => {
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={onDelete}
      />
    </div>
  );
}
